import { Link, useParams } from "@tanstack/react-router";

import { usePageData } from "../lib/api";
import {
  formatAiring,
  formatEpisodeDate,
  type ShowPageData,
  showPagePath,
} from "../lib/publicPages";

/**
 * One show, below the station's shared header (see StationLayout).
 *
 * The endpoint hands back the station too, but the header already has it;
 * only the show's own material is rendered here.
 */
const ShowPage = () => {
  const { stationSlug, showSlug } = useParams({
    from: "/$stationSlug/shows/$showSlug",
  });
  const state = usePageData<ShowPageData>(showPagePath(stationSlug, showSlug));

  if (state.kind === "loading") {
    return <p className="station__quiet mono">Loading…</p>;
  }
  if (state.kind !== "ready") {
    return (
      <div className="station__detail">
        <h2 className="station__detail-name">
          {state.kind === "missing"
            ? "No such show."
            : "Couldn't load this show."}
        </h2>
        <p className="station__blurb">
          {state.kind === "missing"
            ? "This station has no show at this address — it may have been renamed."
            : "Try again in a moment."}
        </p>
        <Link className="btn" to="/$stationSlug/shows" params={{ stationSlug }}>
          All shows
        </Link>
      </div>
    );
  }

  const { show, upcoming, episodes } = state.data;

  return (
    <div className="station__detail">
      {show.image_url && (
        <img className="station__detail-art" src={show.image_url} alt="" />
      )}

      <h2 className="station__detail-name">{show.name}</h2>

      {show.hosts.length > 0 && (
        <p className="station__sub">
          With{" "}
          {show.hosts.map((host, index) => (
            <span key={host.slug}>
              {index > 0 && (index === show.hosts.length - 1 ? " and " : ", ")}
              <Link
                to="/$stationSlug/djs/$djSlug"
                params={{ stationSlug, djSlug: host.slug }}
              >
                {host.name}
              </Link>
            </span>
          ))}
        </p>
      )}

      {show.description && <p className="station__blurb">{show.description}</p>}

      {upcoming.length > 0 && (
        <div className="station__recent">
          <span className="eyebrow">Upcoming</span>
          <ol className="station__tracks">
            {upcoming.map((airing) => (
              <li key={airing.start}>
                <span className="station__track">
                  {formatAiring(airing.start)}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {episodes.length > 0 && (
        <div className="station__recent">
          <span className="eyebrow">Recent episodes</span>
          {/* Titles only; each episode's page carries the player. */}
          <ul className="station__episodes">
            {episodes.map((episode) => (
              <li key={episode.id}>
                <Link
                  to="/$stationSlug/shows/$showSlug/episodes/$episodeId"
                  params={{ stationSlug, showSlug, episodeId: episode.id }}
                >
                  <span className="station__episode-title">
                    {episode.title}
                  </span>
                  <span className="station__when mono">
                    {formatEpisodeDate(episode.start_date)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            className="station__more mono"
            to="/$stationSlug/shows/$showSlug/episodes"
            params={{ stationSlug, showSlug }}
          >
            All episodes
          </Link>
        </div>
      )}

      {show.url && (
        <nav className="station__links mono" aria-label="Show links">
          <a href={show.url}>show site</a>
        </nav>
      )}
    </div>
  );
};

export default ShowPage;
